import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalStorage } from '../utils/LocalStorage';
import { ScreenTracker } from '../utils/ScreenTracker';
import './FreeTrialBanner.scss';
import {CLARITY_UNMASK} from '../utils/clarity/unmask';
import {useTrialCodeAvailability} from './trial_code/TrialCodeForm';
import {TrialCodeModal} from './trial_code/TrialCodeModal';

const FREE_TRIAL_STATUS = 'Free Trial';
const MAX_DAYS_TO_SHOW = 5;
const REFRESH_INTERVAL_MS = 5000;

// El túnel del alta y el wizard de líneas son una capa fija a pantalla completa (`.ob-tunnel`, `z-index: 1`)
// y la pila de banners (`z-index: 1000`) se les montaba encima del encabezado, con «Guardar y seguir
// después» incluido. Ahí el banner no se dibuja: el túnel ya guía el mismo paso (review C-ms-fe-01).
const FULLSCREEN_PATH_PREFIXES = ['/onboarding', '/lines/new'];

function isFullscreenPath(pathname) {
    return FULLSCREEN_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

// El próximo paso de la prueba (`trial_next_step` del backend): qué dice el banner, a qué pantalla lleva
// su botón y con qué nombre se cuenta el clic. Es el mismo paso que piden el correo y el WhatsApp del
// seguimiento (MS-8): el banner lo repite. Las rutas son las del drawer de primeros pasos
// (`onboarding/drawer/setupStatus.js`), y un test lo vigila. Un paso que este código no conoce no se
// dibuja: el banner vuelve al de siempre.
//
// «Asignar cuentas» va a `/onboarding/clientes`, el paso del alta que arma los clientes con las cuentas
// conectadas. Hasta el 31/10 iba al Listado, donde no se vincula nada, y desde el Listado el botón no
// hacía nada: una agencia lo tocó 14 veces sin vincular su cuenta (UH-14 de `/ux`).
export const TRIAL_STEPS = {
    CONECTAR_PLATAFORMA: {
        path: '/onboarding/plataformas',
        textKey: 'free_trial_banner.step_conectar_plataforma',
        ctaKey: 'free_trial_banner.step_cta_conectar_plataforma',
        event: 'trial_banner_click_conectar_plataforma',
    },
    ASIGNAR_CUENTAS: {
        path: '/onboarding/clientes',
        textKey: 'free_trial_banner.step_asignar_cuentas',
        ctaKey: 'free_trial_banner.step_cta_asignar_cuentas',
        event: 'trial_banner_click_asignar_cuentas',
    },
    CONECTAR_IA: {
        path: '/onboarding/ia',
        textKey: 'free_trial_banner.step_conectar_ia',
        ctaKey: 'free_trial_banner.step_cta_conectar_ia',
        event: 'trial_banner_click_conectar_ia',
    },
    ELEGIR_PLAN: {
        path: '/agency/subscription',
        textKey: 'free_trial_banner.step_elegir_plan',
        ctaKey: 'free_trial_banner.step_cta_elegir_plan',
        event: 'trial_banner_click_elegir_plan',
    },
};

function knownStep(step) {
    return step && TRIAL_STEPS[step.code] ? step : null;
}

function sameStep(a, b) {
    return a?.code === b?.code && a?.days_remaining === b?.days_remaining;
}

export const FreeTrialBanner = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [daysRemaining, setDaysRemaining] = useState(null);
    const [agencyStatus, setAgencyStatus] = useState(null);
    const [nextStep, setNextStep] = useState(null);
    const [codeModalOpen, setCodeModalOpen] = useState(false);
    // «¿Tienes un código?» (CY-T, el Cyber 2026): sólo si a la agencia le sirve uno hoy. Se pregunta con la
    // prueba en curso; el backend contesta que no (o un 404) al que no es admin.
    const isTrialAgencyUser = LocalStorage.fetchUserType() === 'AGENCY_USER' && agencyStatus === FREE_TRIAL_STATUS;
    const [codeAvailable, setCodeAvailable] = useTrialCodeAvailability(isTrialAgencyUser);
    const codeAutoOpened = useRef(false);

    useEffect(() => {
        const readFromStorage = () => {
            setDaysRemaining(LocalStorage.fetchFreeTrialDaysRemaining());
            setAgencyStatus(LocalStorage.fetchAgencyStatus());
            // Un objeto nuevo en cada lectura re-renderizaría el banner cada 5 s sin que nada cambie.
            const stored = knownStep(LocalStorage.fetchTrialNextStep());
            setNextStep((current) => (sameStep(current, stored) ? current : stored));
        };

        readFromStorage();
        const interval = setInterval(readFromStorage, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    // Quien llegó por `/signup?codigo=…` con la cuenta ya creada (o a quien el alta no le pudo aplicar el
    // código) tiene el código guardado: si le sirve, el modal se abre solo, prellenado, una vez por carga y
    // nunca encima del túnel (review C-cy-t-10). Sin esto quedaba escondido detrás del link, sin aviso.
    const fullscreen = isFullscreenPath(location.pathname);
    useEffect(() => {
        if (!codeAvailable || fullscreen || codeAutoOpened.current || !LocalStorage.getSignupTrialCode()) return;
        codeAutoOpened.current = true;
        ScreenTracker.recordEvent('trial_code_open_signup_link');
        setCodeModalOpen(true);
    }, [codeAvailable, fullscreen]);

    const openCodeModal = () => {
        ScreenTracker.recordEvent('trial_code_open_banner');
        setCodeModalOpen(true);
    };

    // Aplicado, el acceso desaparece y el banner relee los días ya: no espera a su próximo ciclo de 5 s.
    const handleCodeRedeemed = () => {
        setCodeAvailable(false);
        setDaysRemaining(LocalStorage.fetchFreeTrialDaysRemaining());
        setNextStep(knownStep(LocalStorage.fetchTrialNextStep()));
    };

    // El modal vive aunque el banner deje de dibujarse con él abierto (la prueba cambió de estado al aplicar
    // el código), y siempre en el MISMO lugar del árbol: en otro, React lo remonta y pierde el «Listo».
    const withCodeModal = (banner) => (
        <>
            {banner}
            <TrialCodeModal isOpen={codeModalOpen} onClose={() => setCodeModalOpen(false)}
                            onRedeemed={handleCodeRedeemed}/>
        </>
    );
    const hidden = () => withCodeModal(null);

    // Sólo para el equipo de la agencia: el backend manda el estado de la agencia también al usuario del
    // portal, y el cliente no tiene por qué enterarse de la prueba de su agencia con Dashbo (C-ms-fe-03).
    if (LocalStorage.fetchUserType() !== 'AGENCY_USER') return hidden();
    if (agencyStatus !== FREE_TRIAL_STATUS || LocalStorage.fetchHasPaymentSuspensionWarning()) return hidden();
    if (fullscreen) return hidden();

    // Con el próximo paso, el banner sale desde el primer día; sin él, como siempre: los últimos días.
    const days = nextStep && Number.isInteger(nextStep.days_remaining) ? nextStep.days_remaining : daysRemaining;
    if (days === null) return hidden();
    if (!nextStep && days > MAX_DAYS_TO_SHOW) return hidden();

    const getMessage = () => {
        if (daysRemaining === 0) return t('free_trial_banner.message_0');
        if (daysRemaining === 1) return t('free_trial_banner.message_1');
        return t('free_trial_banner.message', { days: daysRemaining });
    };

    const getDaysSentence = () => {
        if (days === 0) return t('free_trial_banner.step_days_0');
        if (days === 1) return t('free_trial_banner.step_days_1');
        return t('free_trial_banner.step_days', { days });
    };

    const step = nextStep ? TRIAL_STEPS[nextStep.code] : null;

    const handleCtaClick = () => {
        if (!step) {
            navigate('/agency/subscription');
            return;
        }
        ScreenTracker.recordEvent(step.event);
        navigate(step.path);
    };

    return withCodeModal(
        <div className="free-trial-banner" data-step={nextStep ? nextStep.code : undefined}>
            <div className="free-trial-banner-content">
                <span className="free-trial-banner-text">
                    {step ? `${getDaysSentence()} ${t(step.textKey)}` : getMessage()}
                </span>
                <button className="free-trial-banner-cta" onClick={handleCtaClick} {...CLARITY_UNMASK}>
                    {step ? t(step.ctaKey) : t('free_trial_banner.cta')}
                </button>
                {codeAvailable && (
                    <button type="button" className="free-trial-banner-code" onClick={openCodeModal} {...CLARITY_UNMASK}>
                        {t('trial_code.banner_link')}
                    </button>
                )}
            </div>
        </div>
    );
};
